import "./App.css";
import {useEffect, useMemo, useState} from "react";
import {premiums} from "./constants/premiums";
import {paymentMethods, SPECIAL_METHODS} from "./constants/paymentMethods";
import {getApiUrl, getCountryUrl, validateAccount} from "./helpers";
import {
    AccountValidationState,
    mapValidationStateToCSS,
    getValidationStateTitle
} from "./constants/accountValidationStates";
import {PERSIST_USER_ID_KEY, USER_ID_LENGTH} from "./constants/commonConstants";
import ClipLoader from "react-spinners/ClipLoader";
import {Button} from "./components/Button";
import {useSearchParams} from "react-router-dom";
import {useTranslation} from "./i18n/LanguageProvider";

function App() {
    const [userId, setUserId] = useState('');
    const [searchParams] = useSearchParams();
    const [activePremium, setActivePremium] = useState('month');
    const [method, setMethod] = useState(null);
    const [emptyUserIdError, setEmptyUserIdError] = useState(false);
    const [validationState, setAccountValidationState] = useValidationState(userId);
    const [isFetching, setIsFetching] = useState(false);
    const [userCountry, setUserCountry] = useState(null);
    const appVersion = searchParams.get('ver');
    const {t, language} = useTranslation();

    const translateWithFallback = (key, fallbackValue) => {
        const translated = t(key);
        return translated === key ? fallbackValue : translated;
    };

    const orderedPaymentMethods = useMemo(() => {
        const isRussianContext = language === 'ru' || userCountry === 'RU';
        // Filter methods that require Russian context (language or IP)
        let methods = paymentMethods.filter(m => !m.requiresRu || isRussianContext);

        if (language === 'en') {
            methods = methods.filter((method) => method.identifier !== 'card');
            // For English users: put Stripe first
            const stripeMethod = methods.find((method) => method.identifier === 'stripe');
            if (stripeMethod) {
                const rest = methods.filter((method) => method.identifier !== 'stripe');
                methods = [stripeMethod, ...rest];
            }
        }
        return methods;
    }, [language, userCountry]);

    const validateAccountAndUpdateState = (userId) => {
        validateAccount(userId)
            .then((res) => {
                if (res) {
                    setAccountValidationState(AccountValidationState.CORRECT_ID);
                } else {
                    setAccountValidationState(AccountValidationState.WRONG_ID);
                }
            })
            .catch(() => setAccountValidationState(AccountValidationState.WRONG_ID));
    }
    useEffect(() => {
        const searchParamsUserId = searchParams.get('userId');
        const localStorageUserId = localStorage.getItem(PERSIST_USER_ID_KEY);
        const persistedUserId = searchParamsUserId || localStorageUserId;
        if (persistedUserId) {
            // Restore persisted uid on mount
            setUserId(persistedUserId);
            validateAccountAndUpdateState(persistedUserId);
        }
        const paramPremium = searchParams.get('premium');
        if (paramPremium && premiums.find((p) => p.identifier === paramPremium)) {
            setActivePremium(paramPremium);
        }
        // Detect user country
        fetch(getCountryUrl())
            .then(res => res.json())
            .then(data => setUserCountry(data.country))
            .catch(() => {});
        // Runs once on mount: it reads the initial URL and storage, and re-running would re-validate on every render
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // Change payment method to available one if user can't use the selected one
        const switchPaymentMethodIfNeeded = () => {
            if (!method) {
                return;
            }
            const isSpecialMethod = SPECIAL_METHODS.includes(method);
            if (!isSpecialMethod) {
                // Don't do anything if method is not a special one because the rest of the methods have no limitations
                return;
            }
            // Find active premium item
            const activePremiumItem = premiums.find((p) => p.identifier === activePremium);
            if (!activePremiumItem) {
                // Should never happen
                return;
            }
            const canPayWithSpecialMethod = activePremiumItem.price >= 1000;
            if (!canPayWithSpecialMethod) {
                // If user can't pay with special method switch to the first available method
                const availableMethods = orderedPaymentMethods.filter(
                    (pm) => !SPECIAL_METHODS.includes(pm.identifier)
                );
                if (!availableMethods.length) {
                    return;
                }
                setMethod(availableMethods?.[0]?.identifier);
            }
        }
        switchPaymentMethodIfNeeded();
    }, [activePremium, method, orderedPaymentMethods]);

    useEffect(() => {
        const defaultMethod = orderedPaymentMethods[0]?.identifier;
        if (!defaultMethod) {
            return;
        }
        const methodExistsInList = orderedPaymentMethods.some((item) => item.identifier === method);
        if (!method || !methodExistsInList) {
            setMethod(defaultMethod);
        }
    }, [orderedPaymentMethods, method]);

    async function onFormSubmit(event) {
        event.preventDefault();

        if (AccountValidationState.CORRECT_ID !== validationState) {
            return;
        }

        if (userId.length === 0) {
            setEmptyUserIdError(true);
            return;
        }
        // If all checks were passed then persist uid
        localStorage.setItem(PERSIST_USER_ID_KEY, userId);
        setIsFetching(true);
        const premium = premiums.find((p) => p.identifier === activePremium);
        const urlParams = new URLSearchParams({
            userId,
            method,
            appVersion,
            premium: premium.identifier,
            language,
        });
        fetch(getApiUrl() + `?${urlParams.toString()}`)
            .then(async (response) => {
                if (response.status === 200) {
                    const {url} = await response.json();
                    // Navigate without sending referrer
                    const link = document.createElement('a');
                    link.href = url;
                    link.rel = 'noreferrer noopener';
                    link.target = '_self';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                } else {
                    const responseDetails = await response.text();
                    alert(t('app.alert.paymentLinkFailed', {
                        status: response.statusText,
                        details: responseDetails,
                    }));
                }
            })
            .catch(() => {
                alert(t('app.alert.generic'));
            })
            .finally(() => setIsFetching(false));
    }

    return (
        <div className={'App-container'}>
            <form onSubmit={onFormSubmit}>
                <h1 className={'App-header'}>
                    {t('app.header.prefix')} <span>anon.chat</span> {t('app.header.suffix')}
                </h1>
                <Section>
                    <Title>
                        {t('app.enterAccountId.title')}
                    </Title>
                    <Subtitle>
                        {t('app.enterAccountId.subtitle.part1')}
                        <a href={"anonchat://settings/about/system-info?buyPremium=true"}>
                            {t('app.enterAccountId.subtitle.linkText')}
                        </a>
                    </Subtitle>
                    <div className={'App-input-user-id-container'}>
                        <input
                            className={
                                `App-input-user-id
                                ${emptyUserIdError && AccountValidationState.CORRECT_ID !== validationState && 'invalid'} 
                                ${AccountValidationState.NONE !== validationState && 'extra-padding'}`
                            }
                            type="text"
                            name="userid"
                            placeholder={'b62a7e9f-cb71-41a8-9a7c-30e01fb37cb6'}
                            value={userId}
                            onChange={(e) => {
                                setAccountValidationState(AccountValidationState.NONE);
                                setUserId(e.target.value?.trim());
                                // If user removes input's value then remove persisted uid
                                if (e.target.value?.trim() === "") {
                                    localStorage.removeItem(PERSIST_USER_ID_KEY);
                                }
                            }}
                            onFocus={(event) => {
                                // Wrapping select() in setTimeout to make it work in Safari
                                // https://stackoverflow.com/questions/54229359/why-does-select-not-work-on-safari-with-reactjs
                                setTimeout(() => event.target.select(), 0);
                            }}
                            onBlur={() => {
                                if (validationState !== AccountValidationState.NONE) {
                                    // If validation state already exists, no need to validate again
                                    return;
                                }

                                if (userId.length !== 0) {
                                    setAccountValidationState(AccountValidationState.VALIDATING);
                                    validateAccountAndUpdateState(userId);
                                } else {
                                    setAccountValidationState(AccountValidationState.NONE);
                                }
                            }}
                        />
                        {validationState !== AccountValidationState.NONE &&
                            <span
                                className={`App-account-validating-state ${mapValidationStateToCSS[validationState]}`}>
                            {validationState === AccountValidationState.VALIDATING && <ClipLoader
                                size={15}
                                color={'#6D84E4'}
                                cssOverride={{marginRight: 5}}
                            />}
                                {getValidationStateTitle(validationState, t)}
                        </span>}
                    </div>
                    {emptyUserIdError && AccountValidationState.CORRECT_ID !== validationState &&
                        <label className={'App-input-invalid-label'}>{t('app.emptyUserIdError')}</label>}
                </Section>
                <Section>
                    <Title>
                        {t('app.selectPlan')}
                    </Title>
                    {premiums.map((item) => {
                        const {identifier, fancyName} = item;
                        const isChecked = identifier === activePremium;
                        const planTitleKey = `premium.${identifier}.title`;
                        const planTitle = translateWithFallback(planTitleKey, fancyName);
                        return <SelectableBox
                            key={identifier}
                            identifier={identifier}
                            onClick={setActivePremium}
                            isChecked={isChecked}
                            title={planTitle}
                            subtitle={item.getFancyPrice(language)}
                        />;
                    })}
                </Section>
                <Section>
                    <Title>
                        {t('app.selectPaymentMethod')}
                    </Title>
                    {orderedPaymentMethods.map(({identifier, title, extra}) => {
                        const isChecked = identifier === method;
                        const activePremiumItem = premiums.find((p) => p.identifier === activePremium);
                        const canPayWithSpecialMethod = activePremiumItem.price >= 1000;
                        const isSpecialMethod = SPECIAL_METHODS.includes(identifier);
                        // Users can pay with special method only if price is higher than 1000 RUB
                        const isDisabled = isSpecialMethod && !canPayWithSpecialMethod;
                        const paymentTitleKey = `payment.methods.${identifier}.title`;
                        const paymentExtraKey = `payment.methods.${identifier}.extra`;
                        const translatedTitle = translateWithFallback(paymentTitleKey, title);
                        const translatedExtra = (() => {
                            const candidate = t(paymentExtraKey);
                            // If key not found, use fallback. If found (even empty string), use translation
                            if (candidate === paymentExtraKey) {
                                return extra;
                            }
                            return candidate;
                        })();
                        return <SelectableBox
                            key={identifier}
                            identifier={identifier}
                            onClick={setMethod}
                            isChecked={isChecked}
                            isDisabled={isDisabled}
                            title={translatedTitle}
                            subtitle={translatedExtra}
                        />;
                    })}
                    <Button
                        onClick={onFormSubmit}
                        title={t('app.submit')}
                        isDisabled={AccountValidationState.CORRECT_ID !== validationState}
                        isFetching={isFetching}
                    />
                    {validationState === AccountValidationState.WRONG_ID && <span className={'App-wrong-user-id-error'}>
                {t('app.wrongUserIdDescription')}
            </span>}
                </Section>
            </form>
        </div>
    );
}

const Section = ({children}) => <div className={'App-Section'}>
    {children}
</div>

const Title = ({children}) => <p className={'App-title'}>
    {children}
</p>;

const Subtitle = ({children}) => <p className={'App-subtitle'}>
    {children}
</p>

const RadioButton = ({isChecked}) => <div className={'App-radio-button-outside'}>
    <div className={`${isChecked && 'App-radio-button-active'}`}/>
</div>;

const SelectableBox = ({identifier, isChecked, onClick, title, subtitle, isDisabled = false}) => <div
    key={identifier}
    className={`App-selectable-box ${isChecked && 'active'} ${isDisabled && 'disabled'}`}
    onClick={() => onClick(identifier)}
>
    <RadioButton isChecked={isChecked}/>
    <span className={'App-selectable-box-title'}>{title}</span>
    <Spacer/>
    <span className={'App-selectable-box-subtitle'}>{subtitle}</span>
</div>;

const Spacer = () => <div className={'spacer'}/>;


const useValidationState = (userId) => {
    const [accountValidationState, setAccountValidationState] = useState(AccountValidationState.NONE);
    useEffect(() => {
        if (userId.length === USER_ID_LENGTH) {
            setAccountValidationState(AccountValidationState.VALIDATING);
            validateAccount(userId)
                .then((res) => {
                    if (res) {
                        setAccountValidationState(AccountValidationState.CORRECT_ID);
                    } else {
                        setAccountValidationState(AccountValidationState.WRONG_ID);
                    }
                })
                .catch(() => setAccountValidationState(AccountValidationState.WRONG_ID));
        }
    }, [userId]);

    return [accountValidationState, setAccountValidationState];
}


export default App;
